import { Observable } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { Inject, Injectable } from '@angular/core';
import { Account, API_URL, User, UserDetails } from '@frontend-site/shared/data';
import { SessionRepository } from '@frontend-site/shared/core';
import { UserSubscriptions } from '../entities/user-subscriptions';
import { UserProfile } from '../entities/user-profile';
import { UserStatus } from '../entities/user-status.enum';

export interface JobTitleOption {
    id: number;
    key: string;
}

export function findUserStatus(userId: number, inactiveUsers: User[], disconnectedUsers: User[]): UserStatus | null {
    return !userId
        ? null
        : isUserInList(disconnectedUsers, userId)
        ? UserStatus.DISCONNECTED
        : isUserInList(inactiveUsers, userId)
        ? UserStatus.INACTIVE
        : UserStatus.ACTIVE;
}

function isUserInList(usersList: User[], userId: number): boolean {
    return !!userId && usersList.some((user) => user.id === userId);
}

@Injectable({
    providedIn: 'root'
})
export class UserService {
    constructor(
        private readonly http: HttpClient,
        private translate: TranslateService,
        @Inject(API_URL) private readonly apiUrl: string,
        private sessionRepository: SessionRepository
    ) {}

    getUserProfile(companyId?: number): Observable<UserProfile> {
        const params = companyId != null ? new HttpParams().set('companyId', companyId) : undefined;
        return this.http.get<UserProfile>(`${this.apiUrl}/UM/getUserProfileDetails`, { params });
    }

    getUserDetails(): Observable<UserDetails> {
        return this.http.get<UserDetails>(`${this.apiUrl}/pqm/getUserDetails`);
    }

    getCompanies(): Observable<Account[]> {
        return this.http.get<Account[]>(`${this.apiUrl}/pqm/accountList`);
    }

    getUserSubscriptions(companyId: number): Observable<UserSubscriptions> {
        const params = new HttpParams().set('companyId', companyId);
        return this.http.get<UserSubscriptions>(`${this.apiUrl}/UM/getUserSubscriptions/${companyId}`, {
            params
        });
    }

    getJobTitles(): Observable<JobTitleOption[]> {
        return this.http.get<JobTitleOption[]>(`${this.apiUrl}/UM/getJobTitles`);
    }

    changeProfile(user: UserProfile): Observable<{ result: string }> {
        return this.http.post<{ result: string }>('/rest/UM/changeProfile', user);
    }

    validatePassword(passValue: string, newPassValue: string, reNewPassValue: string): [boolean, string, string] {
        let errMsg = '';
        let errMsgInvalid = '';
        let pass: boolean;
        if (!passValue && !newPassValue && !reNewPassValue) {
            pass = true;
        } else if (passValue && newPassValue && reNewPassValue) {
            if (newPassValue !== reNewPassValue) {
                errMsg = this.translate.instant('UserProfile.errMsgPasswordDontMatch');
                pass = false;
                return [pass, errMsg, errMsgInvalid];
            }
            if (
                newPassValue &&
                newPassValue.length >= 8 &&
                newPassValue.toUpperCase() != newPassValue &&
                newPassValue.toLowerCase() != newPassValue &&
                newPassValue.match(/\d+/g)
            ) {
                pass = true;
            } else {
                errMsgInvalid = this.translate.instant('UserProfile.errMsgPasswordInvalid');
                pass = false;
            }
        } else {
            pass = false;
            errMsg = this.translate.instant('UserProfile.errMsgPasswordNotFilled');
        }
        return [pass, errMsg, errMsgInvalid];
    }

    isExecuteIaUser(): boolean {
        const host = location.host || '';
        const protocol = location.protocol || '';
        const currentUserEmail = this.sessionRepository.applicationConfig?.user.email || '';
        return (
            currentUserEmail.endsWith('@execute.ia') &&
            protocol.startsWith('https') &&
            (host.startsWith('my.panaya.com') || host.startsWith('emea.panaya.com') || host.startsWith('qa6-appprod.rnd.panaya.int'))
        );
    }
}
